<template>
  <div class="content">
    <div class="card header">
      <div class="my-select-commodity">
        <a-input-group compact>
          <a-select v-model:value="queryBy"
                    :options="queryByOptions"> </a-select>
          <a-input v-if="queryBy === 'name'"
                   v-model:value="query.name"
                   :placeholder="$t('CustomSpus.CustomSpus.inputPlaceholder')"
                   style="width: 60%"
                   @press-enter="onSearch" />
          <a-input v-else
                   v-model:value="query.code"
                   :placeholder="$t('CustomSpus.CustomSpus.inputPlaceholder')"
                   style="width: 60%" />
        </a-input-group>
      </div>
      <div class="my-select-me">
        <a-input-group>
          <span style="margin-right: 10px">{{ $t('CustomSpus.CustomSpus.productCategory') }}</span>
          <pagination-select data-key="spus"
                             item-name="name"
                             item-id="id"
                             :search-enable="true"
                             :allow-clear="true"
                             style="width: 100%"
                             size="default"
                             :value="selectedSpu"
                             :fetch-data="fetchSpus"
                             :placeholder="$t('CustomSpus.CustomSpus.selectProductCategory')"
                             @change="onSelectSpu"> </pagination-select>
        </a-input-group>
      </div>
      <div class="buttonGroup">
        <a-button @click="onSearch"> <search-outlined />{{ $t('CustomSpus.CustomSpus.search') }} </a-button>
        <a-button @click="onRestQuery"> <redo-outlined />{{ $t('CustomSpus.CustomSpus.resetAll') }} </a-button>
        <a-button @click="gotoSups"> <plus-square-outlined />{{ $t('CustomSpus.CustomSpus.createProduct') }} </a-button>
        <a-button type="primary"
                  danger
                  class="dangerStyle"
                  @click="batchDelete"> <delete-outlined />{{ $t('CustomSpus.CustomSpus.delete') }} </a-button>
      </div>
    </div>
    <div class="card table">
      <div ref="hiddenContainer"
           class="hidden"></div>
      <a-scroll-table class="scrollTable"
                      :row-selection="rowSelection"
                      :columns="columns"
                      :loading="loading"
                      :data-source="customSpus"
                      :pagination="false"
                      row-key="id">
        <template #bodyCell="{ column, text, record }">
          <template v-if="column.dataIndex === 'customizedImages'">
            <div v-if="hiddenContainer"
                 class="viewer">
              <CustomizedImagePreview :customized-data="mergeCustomAreaData(record)" />
            </div>
          </template>
          <template v-if="column && column.dataIndex === 'spu'">
            {{ text.name }}
          </template>
          <template v-if="column && column.dataIndex === 'price'">
            <a-popover placement="topLeft">
              <div>{{ record.price }}</div>
              <template #content>
                <price-breakdown :custom-price-detail="record.price_detail"
                                 :is-custom="true"
                                 :is-order="false"></price-breakdown>
              </template>
            </a-popover>
          </template>
          <template v-if="column && column.dataIndex === 'operation'">
            <a-tooltip placement="bottom"
                       :color="'#111'">
              <template #title>
                <span>{{ $t('CustomSpus.CustomSpus.addToCart') }}</span>
              </template><shopping-cart-outlined class="button"
                                      @click="onAddToCart(record)" />
            </a-tooltip>
            <a-tooltip placement="bottom"
                       :color="'#111'">
              <template #title>
                <span>{{ $t('CustomSpus.CustomSpus.details') }}</span>
              </template><file-search-outlined class="button"
                                    @click="details(record)" />
            </a-tooltip>
            <a-tooltip placement="bottom"
                       :color="'#111'">
              <template #title>
                <span>{{ $t('CustomSpus.CustomSpus.share') }}</span>
              </template>
              <ExportOutlined class="button"
                              @click="toShareTheme(record)" />
            </a-tooltip>
            <a-tooltip placement="bottom"
                       :color="'#111'">
              <template #title>
                <span>{{ $t('CustomSpus.CustomSpus.deleteProduct') }}</span>
              </template>
              <delete-outlined class="button"
                               @click="onDeleteCustomSpu(record)" />
            </a-tooltip>
          </template>
        </template>
      </a-scroll-table>
      <div class="footer">
        <div style="display: flex; justify-content: flex-end">
          <APagination v-model:current="pagination.page"
                       v-model:pageSize="pagination.limit"
                       :total="pagination.total"
                       @change="onChange" />
        </div>
      </div>
    </div>
    <ShoppingCartModal ref="shoppingCart" />
  </div>
</template>
<script setup lang="ts">
import { toUrl } from "@/utils"
import { useSeo } from "&/utils/seo"
import { AScrollTable, APagination, CustomizedImagePreview, PaginationSelect } from "@iota-cn/ui-components"
import type { TableColumnType, TableProps } from "ant-design-vue"
import { Modal } from "ant-design-vue"
import dayjs from "dayjs"
import { onMounted, reactive, ref, nextTick, watch, computed } from "vue"
import { useRouter, useRoute } from "vue-router"
import { useShoppingCart } from "../.."
import { deleteCustomSpu, fetchAllCustomSpus, fetchAllSpus } from "../api"
import type { CustomSpu, Spu, CustomPriceDetail, CustomPrice, Technology } from "../types"
import { convertSpuToSimpleSpu, findCustomableImage } from "../utils"
import ShoppingCartModal from "./components/ShoppingCartModal.vue"
import PriceBreakdown from "./components/PriceBreakdown.vue"
import { container } from "tsyringe"
import type { AccountBaseInfoOption, AccountInfoService } from '../../account-info/index'
import type { Plugin } from "@/plugins"
import { PLUGIN_ACCOUNT_INFO } from "@/plugins/builtin/BuiltinPluginSymbol"
import BigNumber from "bignumber.js"
import { DefaultCalcPriceHelper } from "@flashbase/util-coupon"
import { useI18n } from 'vue-i18n'

// SEO 配置
useSeo({
  title: '定制产品 - Flashbase',
  description: 'Flashbase提供丰富的定制产品，包括T恤、卫衣、帽子等多种品类，支持个性化设计，专业工厂生产',
  keywords: 'Flashbase,定制产品,T恤,卫衣,帽子,个性化设计,定制服务',
  ogType: 'product',
})

const calcPrice = new DefaultCalcPriceHelper()
const accountInfoPlugin = container.resolve<Plugin<AccountBaseInfoOption, AccountInfoService>>(PLUGIN_ACCOUNT_INFO)
const baseDiscount = accountInfoPlugin.options.baseDiscount.value
BigNumber.config({ ROUNDING_MODE: 1 })

const shoppingCartService = useShoppingCart()
const searchTimeoutId = ref()
const { t } = useI18n()

const mergeCustomAreaData = (item: any) => {
  return Object.assign(
    {},
    {
      image: renderImageBaseUrl(item),
      customArea: Object.assign({},
        item.customizedImages[0].customableImage.custom_areas ||
        item.spu_custom_areas || {},
        { boundingRect: item.customizedImages[0].customized_areas.boundingRect }
      ),
      customizedImage: item.customizedImages[0].customized_image + `?x-oss-process=image/resize,w_50/quality,q_90` || "",
      width: 1000,
      height: 1000,
      cssWidth: 50,
      cssHeight: 50,
    },
  )
}

const renderImageBaseUrl = (record: any) => {
  const baseCustomUrl = toUrl(findCustomableImage(color(record), record.customizedImages[0].customableImage))
  const baseHeightRatio = Math.round(record.customizedImages[0].customableImage.custom_areas.bgHeight * (200 / record.customizedImages[0].customableImage.width))
  return baseCustomUrl + `?x-oss-process=image/resize,h_${baseHeightRatio},m_lfit`
}

const columns = computed<TableColumnType[]>(() => [
  {
    title: t('CustomSpus.CustomSpus.product'),
    align: "center",
    dataIndex: "customizedImages",
    width: "10%"
  },
  {
    title: t('CustomSpus.CustomSpus.price'),
    align: "center",
    dataIndex: "price",
    width: 80,
    customCell: () => {
      return {
        style: {
          color: "#DE4A09"
        }
      }
    }
  },
  {
    title: t('CustomSpus.CustomSpus.productName'),
    align: "center",
    dataIndex: "name",
    width: "15%",
  },
  {
    title: t('CustomSpus.CustomSpus.productCode'),
    align: "center",
    dataIndex: "code",
    width: "20%"
  },
  {
    title: t('CustomSpus.CustomSpus.productCategoryLabel'),
    align: "center",
    dataIndex: "spu",
    width: 200
  },
  // {
  //   title: "总销量 ",
  //   align: "center",
  //   dataIndex: "sale_count",
  //   width: "8%",
  //   customRender: () => {
  //     return "-"
  //   }
  // },
  {
    title: t('CustomSpus.CustomSpus.creationTime'),
    align: "center",
    dataIndex: "createdAt"
  },
  {
    title: t('CustomSpus.CustomSpus.operation'),
    align: "center",
    dataIndex: "operation"
  }
])
// 输入框选择器
const queryBy = ref<string>("name")
const queryByOptions = computed(() => [
  {
    label: t('CustomSpus.CustomSpus.productName'),
    value: "name"
  },
  {
    label: t('CustomSpus.CustomSpus.productCode'),
    value: "code"
  }
])

const hiddenContainer = ref()

// 表格数据
const customSpus = ref<CustomSpu[]>()

// 产品品类选择
const selectedSpu = ref<Spu>()

const onSelectSpu = (spu: Spu) => {
  selectedSpu.value = spu
  if (spu) {
    query.spu_id = spu.id
  } else {
    delete query.spu_id
  }
}

// 默认颜色
const color = (customSpu: CustomSpu) => {
  const customSku = customSpu.customSkus[0] || null
  const color =
    customSku?.sku.attrs.filter((el) => {
      return el.attribute.key === "color"
    }) || null
  const value = color ? color[0].object_value : null
  return value
}

// 分页
const pagination = reactive({
  total: 0,
  limit: 10,
  page: 1
})

const route = useRoute()
onMounted(() => {
  const fromData = sessionStorage.getItem('searchCustom')
  if (route.params.from == 'detail' && fromData) {
    const data = JSON.parse(fromData)
    queryBy.value = data.queryBy
    query.name = data.name
    query.code = data.code
    query.spu_id = data.spu_id
    pagination.limit = data.limit
    pagination.page = new BigNumber(data.offset).div(data.limit).plus(1).toNumber()
    selectedSpu.value = data.selectedSpu
    fetchAllData(data)
  } else {
    fetchAllData()
  }
  sessionStorage.clear()
})

const toShareTheme = (value: CustomSpu) => {
  router.push(`/console/products/design_share?customSpuId=${value.id}`)
}

const onChange = (page: number) => {
  pagination.page = page
  fetchAllData()
}
// 请求数据处理
const fetchAllData = async (data?: any) => {
  const condition = {
    limit: pagination.limit,
    offset: (pagination.page - 1) * pagination.limit,
    ...query
  }
  if (queryBy.value === "name") {
    condition.code = undefined
  } else {
    condition.name = undefined
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const res: any = await fetchAllCustomSpus(data || condition)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  res.customSpus.forEach((customSpu: any) => {
    customSpu.createdAt = dayjs(customSpu.createdAt).format("YYYY-MM-DD HH:mm:ss")
    const data = {} as CustomPriceDetail
    data.spu_price = customSpu.spu.price
    // data.discount_amount_scale = customSpu.discount_amount_scale
    // data.customized_areas_scale = customSpu.customized_areas_scale
    const custom_price = [] as Array<CustomPrice>
    customSpu.customizedImages?.forEach((image: any) => {
      if (image.customableImage) {
        let customized_areas_scale_title = ''
        image.customableImage?.scales?.forEach((scale: any) => {
          if (scale.customized_areas_scale == image.customized_areas.customized_areas_scale) {
            customized_areas_scale_title = scale.title
          }
        })
        let minTechnologyPrice: Technology = {
          id: 0,
          name: '',
          price: 0
        }
        if (image.customableImage.technologies?.length) {
          minTechnologyPrice = image.customableImage.technologies[0]
          image.customableImage.technologies.forEach((res: Technology) => {
            if (minTechnologyPrice.price > res.price)
              minTechnologyPrice = res
          })
        }
        let dss_image_price = '0'
        if (customSpu.references && customSpu.references.length > 0) {
          customSpu.references.forEach((ref: any) => {
            if (ref.product_spu_customable_image_id == image.customableImage.id && ref.type == 2) {
              dss_image_price = new BigNumber(ref.customizedImage.ref_price).plus(dss_image_price).toFixed(2)
            }
          })
        }
        custom_price.push({
          id: image.customableImage.id,
          name: image.customableImage.name,
          price: image.customableImage.price,
          discount_amount_scale: image.customized_areas.discount_amount_scale || 1,
          customized_areas_scale: image.customized_areas.customized_areas_scale || 1,
          customized_areas_scale_title,
          technology: minTechnologyPrice,
          dss_image_price
        })
      }
    })
    data.custom_price = custom_price
    data.spu_discount = baseDiscount
    customSpu.price_detail = data
    customSpu.price = calcPrice.calcSinglePrice(data, true)
    convertSpuToSimpleSpu(customSpu)
  })
  customSpus.value = res.customSpus
  pagination.total = res.total
}
const fetchSpus = async (limit: number, offset: number, name: string) => {
  const condition = {
    limit,
    offset,
    name
  }
  return await fetchAllSpus(condition)
}
// 表格多选
const rowId = ref<number[]>([])
const rowSelection: TableProps["rowSelection"] = {
  onChange: (selectedRowKeys, selectedRows) => {
    rowId.value = []
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectedRows.forEach((item: any) => {
      rowId.value.push(item.id)
    })
  }
}

// 创建商品
const gotoSups = () => {
  router.push("/console/products/spus")
}

// 搜索
const loading = ref(false)
interface Condition {
  spu_id: number | undefined
  name: string | undefined
  code: string | undefined
}
const query = reactive({
  spu_id: undefined,
  name: undefined,
  code: undefined
} as Condition)
const onSearch = async () => {
  loading.value = true
  await fetchAllData()
  loading.value = false
}
const onRestQuery = () => {
  query.code = undefined
  query.spu_id = undefined
  query.name = undefined
  onSearch()
}

// 查看详情
const router = useRouter()
const details = (value: CustomSpu) => {
  const data = {
    queryBy: queryBy.value,
    selectedSpu: selectedSpu.value,
    limit: pagination.limit,
    offset: (pagination.page - 1) * pagination.limit,
    ...query
  }
  sessionStorage.setItem('searchCustom', JSON.stringify(data))
  router.push(`/console/products/custom_spus/${value.id}`)
}

// 加入购物车
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const shoppingCart = ref<any>()
const onAddToCart = (value: CustomSpu) => {
  if (value.customizedImages.length && value.price_detail.custom_price && value.price_detail.custom_price.length) {
    value.customizedImages.forEach((r: any) => {
      value.price_detail.custom_price?.forEach((res: any) => {
        if (r.customableImage.name === res.name)
          res.technologies = r.customableImage.technologies
      })
    })
  }
  const data = {
    is_custom: true,
    spu_id: value.spu_id,
    name: value.name,
    spu_name: value.spu.name,
    customizedImages: value.customizedImages,
    image: "",
    skus: value.customSkus,
    custom_spu_id: value.id,
    price_detail: value.price_detail,
    references: value.references
  }
  shoppingCart.value.show()
  nextTick(() => {
    shoppingCart.value.setProduct(data)
  })
}

// 删除
const onDeleteCustomSpu = (value: CustomSpu) => {
  Modal.confirm({
    title: t('CustomSpus.CustomSpus.confirmDeleteProduct'),
    async onOk() {
      deleteCustomSpu({ ids: value.id })
        .then(() => {
          shoppingCartService.fetch()
          fetchAllData()
        })
        .catch(() => {
          //
        })
    },
    onCancel() { }
  })
}
// 批量删除
const batchDelete = async () => {
  if (rowId.value.length !== 0) {
    Modal.confirm({
      title: t('CustomSpus.CustomSpus.confirmDeleteProduct'),
      async onOk() {
        deleteCustomSpu({ ids: rowId.value.join() })
          .then(() => {
            shoppingCartService.fetch()
            fetchAllData()
          })
          .catch(() => {
            //
          })
      },
      onCancel() { }
    })
  }
}

watch(() => [query.name, query.code], () => {
  clearTimeout(searchTimeoutId.value)
  searchTimeoutId.value = setTimeout(() => {
    onSearch()
  }, 500)
})


</script>
<style scoped lang="less">
.footer {
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  justify-content: right;
  align-items: center;
}

.content {
  height: 100%;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .card {
    width: 100%;
    padding: 0 24px 0 24px;
    background-color: #ffffff;
  }

  .header {
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    justify-content: flex-start;

    .my-select-commodity {
      width: 350px;

      :deep(.ant-select-selector) {
        background: #f6f6f6;
      }

      :deep(.ant-input) {
        height: 32px;
      }
    }

    .my-select-me {
      margin-right: 50px;
    }

    .buttonGroup {
      flex: 1;
      display: inline-flex;
      justify-content: flex-end;

      button {
        height: 40px;
        margin-right: 8px;
      }

      .dangerStyle {
        background: #de4a092b;
        color: #de4a09;
      }
    }
  }

  .table {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: 90%;

    .scrollTable {
      flex: 1;
      overflow: hidden;

      .button {
        color: #999999;
        cursor: pointer;
        margin-right: 10px;
        font-size: 20px;
        font-weight: bold;

        &:hover {
          color: #111111;
        }
      }
    }

    .hidden {
      width: 50px;
      height: 50px;
      position: absolute;
      visibility: hidden;
      display: block;
    }

    .viewer {
      width: 100%;
      height: 50px;
      display: flex;
      justify-content: center;
      align-items: center;

      @-moz-document url-prefix() {
        :deep(.fabric-div) {
          width: 50px !important;
          height: 50px !important;
        }

        :deep(.canvas-container) {
          width: 50px !important;
          height: 50px !important;
        }

        :deep(.lower-canvas) {
          width: 50px !important;
          height: 50px !important;
        }

        :deep(.upper-canvas) {
          width: 50px !important;
          height: 50px !important;
        }
      }
    }
  }

  .link-btn {
    border-right: 1px solid #e9e9e9;
  }

  .price-antd {
    color: #faad14;
    margin: 0;
  }
}
</style>
